import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismModal } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import { toLocalTimeInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import GeneralCurrencySelectInput from 'lumen/components/general/currency-select-input';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import HotelFormFieldsLiveupdateCheckbox from 'lumen/components/hotel/form-fields/liveupdate-checkbox';
import HotelAndMarketRatesExportModalCustomExportDateRangePicker from 'lumen/components/hotel-and-market/rates/export-modal/custom-export-date-range-picker';
import HotelAndMarketRatesExportModalExportRadioButtons from 'lumen/components/hotel-and-market/rates/export-modal/export-radio-buttons';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';
import {
  formatUrl,
  mapCompareParamsToAPIParams,
} from 'lumen/utils/rates-request';

interface HotelAndMarketRatesExportModalRatesExportModalSignature {
  Args: {
    cancel: unknown;
    isMarket: unknown;
    params: unknown;
  };
}

export default class HotelAndMarketRatesExportModalRatesExportModal extends Component<HotelAndMarketRatesExportModalRatesExportModalSignature> {
  @service declare ajax: Services['ajax'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked datepickerEndDate = null;
  @tracked datepickerStartDate = null;
  @tracked liveupdateChecked;
  @tracked liveupdateDisabled;
  @tracked selectedCurrency = null;
  @tracked selectedDays = 60;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultCurrency() {
    // @ts-expect-error: Incorrect type
    return this.hotelAndMarketService.hotel.currencyCode;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endDate() {
    // @ts-expect-error: Incorrect type
    if (this.selectedDays === 'custom') {
      return this.datepickerEndDate;
    }

    return this.hotelAndMarketService
      .getLocalTime()
      .add(this.selectedDays, 'day')
      .format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endDateMoment() {
    return toLocalTimeInBrowserTimezone(
      // @ts-expect-error: Incorrect type
      this.endDate,
      this.hotelAndMarketService.timezone,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isExportDisabled() {
    // @ts-expect-error: Incorrect type
    if (this.selectedDays === 'custom') {
      return (
        !this.datepickerStartDate ||
        !this.datepickerEndDate ||
        dayjs(this.datepickerStartDate).isAfter(dayjs(this.datepickerEndDate))
      );
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPastMonth() {
    return (
      !this.startDateMoment.isSame(
        this.hotelAndMarketService.getLocalTime(),
        'month',
      ) &&
      this.startDateMoment.isBefore(
        this.hotelAndMarketService.getLocalTime().startOf('month'),
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get startDate() {
    // @ts-expect-error: Incorrect type
    if (this.selectedDays === 'custom') {
      return this.datepickerStartDate;
    }

    return this.hotelAndMarketService.getLocalTime().format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get startDateMoment() {
    return toLocalTimeInBrowserTimezone(
      // @ts-expect-error: Incorrect type
      this.startDate,
      this.hotelAndMarketService.timezone,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipText() {
    if (this.selectedCurrency === this.defaultCurrency) {
      return null;
    }

    return this.intl.t('rates.liveupdates.tooltip', {
      currency: this.intl.t(`general.currency.${this.defaultCurrency}`),
    });
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // @ts-expect-error: Incorrect type
    this.liveupdateChecked = Boolean(this.args.params?.liveupdateChecked);
    // @ts-expect-error: Incorrect type
    this.liveupdateDisabled = Boolean(this.args.params?.liveupdateDisabled);

    if (this.hotelAndMarketService.hotel) {
      // @ts-expect-error: Incorrect type
      this.selectedCurrency = this.defaultCurrency;
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  endSelection(value): void {
    if (value) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.datepickerEndDate = value.format('YYYY-MM-DD');
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.datepickerEndDate = value;
    }
  }

  @action
  exportHotel(): void {
    const params = {
      hotel_id: this.hotelAndMarketService.hotel?.id,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      ota: this.args.params?.otaId,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      rate_param: this.args.params?.rateParam,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      persons: this.args.params?.maxPersons,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      los: this.args.params?.los,
      from_date_range_start: this.startDateMoment.format('YYYY-MM-DD'),
      from_date_range_end: this.endDateMoment.format('YYYY-MM-DD'),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      mealtype: this.args.params?.mealPlan,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      membershiptype: this.args.params?.membershipType,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      compset_ids: this.args.params?.compsetId,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      platform: this.args.params?.platform,
      currency: this.selectedCurrency,
    };

    // @ts-expect-error: Incorrect type
    if (this.args.params?.platform > PLATFORMS.PLATFORM_DESKTOP) {
      // @ts-expect-error: Incorrect type
      params['allow_labels[]'] = ['no_label', 'mobile_only'];
    }

    // @ts-expect-error: Incorrect type
    if (this.args.params?.roomType !== 'all') {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      params.roomtype = this.args.params?.roomType;
    }

    // @ts-expect-error: Incorrect type
    mapCompareParamsToAPIParams(this.args.params, params);

    if (
      !isComparingRates(
        // @ts-expect-error: Incorrect type
        this.args.params?.compareType,
        // @ts-expect-error: Incorrect type
        this.args.params?.compareValue,
      ) &&
      !this.isPastMonth
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.params?.defaultRateChanges().forEach((ratechange, index) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params[`compare${index + 1}_extract_date`] = ratechange;
      });
    }

    const url = formatUrl(
      '/apigateway/v1/app/download/rates',
      params,
      this.ajax.host,
    );

    if (this.selectedCurrency !== this.defaultCurrency) {
      this.usageAnalytics.track('Non-Default Currency Selected', {
        eventTrigger: 'excel-export',
        defaultCurrency: this.defaultCurrency,
        selectedCurrency: this.selectedCurrency,
      });
    }

    openUrl(url);
    // @ts-expect-error: Incorrect type
    this.args.cancel();
  }

  @action
  exportMarket(): void {
    const params = {
      market_id: this.hotelAndMarketService.market?.id,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      ota: this.args.params?.otaId,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      rate_param: this.args.params?.rateParam,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      persons: this.args.params?.maxPersons,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      los: this.args.params?.los,
      from_date_range_start: this.startDateMoment.format('YYYY-MM-DD'),
      from_date_range_end: this.endDateMoment.format('YYYY-MM-DD'),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      mealtype: this.args.params?.mealPlan,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      membershiptype: this.args.params?.membershipType,
      // @ts-expect-error: Incorrect type
      page: this.args.params?.page - 1,
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.params?.brands?.length) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      params['brand[]'] = this.args.params.brands;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.params?.managementTypes?.length) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      params['management_type[]'] = this.args.params.managementTypes;
    }

    // @ts-expect-error: Incorrect type
    if (this.args.params?.roomType !== 'all') {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      params.roomtype = this.args.params.roomType;
    }

    // @ts-expect-error: Incorrect type
    mapCompareParamsToAPIParams(this.args.params, params);

    if (
      !isComparingRates(
        // @ts-expect-error: Incorrect type
        this.args.params?.compareType,
        // @ts-expect-error: Incorrect type
        this.args.params?.compareValue,
      ) &&
      !this.isPastMonth
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.params?.defaultRateChanges().forEach((ratechange, index) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params[`compare${index + 1}_extract_date`] = ratechange;
      });
    }

    const url = formatUrl(
      '/apigateway/v1/app/download/rates/summaries',
      params,
      this.ajax.host,
    );

    openUrl(url);
    // @ts-expect-error: Incorrect type
    this.args.cancel();
  }

  @action
  handleExport(): void {
    if (this.liveupdateChecked) {
      this.liveupdate.startLiveRateShop(
        true,
        this.startDateMoment.format('YYYY-MM-DD'),
        this.endDateMoment.format('YYYY-MM-DD'),
      );
      // @ts-expect-error: Incorrect type
      this.args.cancel();
    } else if (this.hotelAndMarketService.hotel) {
      this.exportHotel();
    } else {
      this.exportMarket();
    }
  }

  @action
  onLiveupdateCheckedChange(): void {
    this.liveupdateChecked = !this.liveupdateChecked;
  }

  @action
  // @ts-expect-error: Incorrect type
  setSelectedDays(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (event.target.value === 'custom') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.selectedDays = event.target.value;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      this.selectedDays = parseInt(event.target.value, 10);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  startSelection(value): void {
    if (value) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.datepickerStartDate = value.format('YYYY-MM-DD');
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.datepickerStartDate = value;
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  updateCurrency(currency): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.selectedCurrency = currency.id;

    if (this.selectedCurrency !== this.defaultCurrency) {
      this.liveupdateChecked = false;
      this.liveupdateDisabled = true;
    } else {
      this.liveupdateDisabled = false;
    }
  }

  <template>
    {{! @glint-expect-error: Incorrect type }}
    <PrismModal @onClose={{@cancel}} @title={{t "rates.custom_export"}}>
      <:default>
        <div class="modal--small modal--padded flex flex-col gap-400">
          <HotelAndMarketRatesExportModalExportRadioButtons
            @selectedDays={{this.selectedDays}}
            @setSelectedDays={{this.setSelectedDays}}
          />
          {{! @glint-expect-error: Incorrect type }}
          {{#if (eq this.selectedDays "custom")}}
            <HotelAndMarketRatesExportModalCustomExportDateRangePicker
              @endDate={{this.datepickerEndDate}}
              @endDateMoment={{this.endDateMoment}}
              @endSelection={{this.endSelection}}
              @liveupdateChecked={{this.liveupdateChecked}}
              @params={{@params}}
              @startDate={{this.datepickerStartDate}}
              @startDateMoment={{this.startDateMoment}}
              @startSelection={{this.startSelection}}
            />
          {{/if}}

          {{#unless @isMarket}}
            <div class="divider-line m-0"></div>

            <div class="flex w-full">
              <GeneralCurrencySelectInput
                @isSecondary={{true}}
                @onCurrencyChange={{this.updateCurrency}}
                {{! @glint-expect-error: Incorrect type }}
                @selectedCurrencyCode={{this.selectedCurrency}}
              />
            </div>
            <div class="divider-line m-0"></div>

            <HotelFormFieldsLiveupdateCheckbox
              @days={{this.selectedDays}}
              {{! @glint-expect-error: Incorrect type }}
              @hasLiveShop={{@params.hasLiveShop}}
              @liveupdateChecked={{this.liveupdateChecked}}
              @liveupdateDisabled={{this.liveupdateDisabled}}
              @onChangeChecked={{this.onLiveupdateCheckedChange}}
              @tooltipText={{this.tooltipText}}
            />
          {{/unless}}
        </div>
      </:default>
      <:footer>
        <div class="settings-modal__footer">
          <GeneralFormFieldsCancelAndConfirm
            {{! @glint-expect-error: Incorrect type }}
            @cancel={{@cancel}}
            @confirm={{this.handleExport}}
            @confirmText={{if
              this.liveupdateChecked
              (t "settings.reports.run")
              (t "general.export")
            }}
            @isConfirmDisabled={{this.isExportDisabled}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
